import type { LearnedTimelinePoint } from "@-/dev.improve.haniverse.lib.learning-stats.entities";
import { hasLength } from "@-/com.npmjs.ts-array-length";

export type Timeframe = "1W" | "1M" | "1Y" | "All";
export const TIMEFRAMES: readonly Timeframe[] = ["1W", "1M", "1Y", "All"];
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function timeframeCutoffMs(timeframe: Timeframe): number | null {
  const now = Date.now();
  switch (timeframe) {
    case "1W": {
      return now - 7 * MS_PER_DAY;
    }
    case "1M": {
      return now - 30 * MS_PER_DAY;
    }
    case "1Y": {
      return now - 365 * MS_PER_DAY;
    }
    default: {
      return null;
    }
  }
}

/** Local-midnight epoch ms of a `YYYY-MM-DD` day key. */
export function dayKeyToMs(dayKey: string): number {
  const [y = 1970, m = 1, d = 1] = dayKey.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).getTime();
}

function msToDayKey(ms: number): string {
  const date = new Date(ms);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function filterTimeline(
  timeline: readonly LearnedTimelinePoint[],
  timeframe: Timeframe,
): readonly LearnedTimelinePoint[] {
  const cutoff = timeframeCutoffMs(timeframe);
  if (cutoff === null) {
    return timeline;
  }
  // Carry the most recent in-window state forward by keeping the last
  // pre-cutoff point as the starting baseline so the line doesn't visually
  // drop to zero at the cutoff.
  const lastBeforeCutoff = [...timeline].reverse().find((p) => dayKeyToMs(p.dayKey) < cutoff);
  const inWindow = timeline.filter((p) => dayKeyToMs(p.dayKey) >= cutoff);
  if (lastBeforeCutoff !== undefined) {
    return [{ ...lastBeforeCutoff, dayKey: msToDayKey(cutoff) }, ...inWindow];
  }
  return inWindow;
}

/**
 * Pad a (possibly windowed) timeline so the line chart always has at least two points to
 * connect: a trailing "today" point that carries the most recent cumulative value forward, and —
 * only when the series starts at the user's very first learning day — a leading zero baseline one
 * day before it. Without this, single-day users see a lone dot floating in an otherwise empty
 * chart.
 *
 * The zero baseline is deliberately skipped for a window that starts mid-history
 * (`filterTimeline` carries the pre-cutoff total forward as the first point): prepending 0 there
 * draws a fake cliff from nothing up to the carried total on the window's first day, and makes
 * the "change over this window" read as the lifetime total.
 */
export function padTimelineForChart(
  timeline: readonly LearnedTimelinePoint[],
  { zeroBaseline = true }: { zeroBaseline?: boolean } = {},
): readonly LearnedTimelinePoint[] {
  if (hasLength(timeline, 0)) {
    return timeline;
  }
  const first = timeline[0];
  const last = timeline.at(-1);
  if (first === undefined || last === undefined) {
    return timeline;
  }
  const todayKey = msToDayKey(Date.now());
  const baselineKey = msToDayKey(dayKeyToMs(first.dayKey) - MS_PER_DAY);

  const padded: LearnedTimelinePoint[] = zeroBaseline
    ? [{ coveragePercent: 0, dayKey: baselineKey, learnedCount: 0 }, ...timeline]
    : [...timeline];
  if (last.dayKey !== todayKey) {
    padded.push({ ...last, dayKey: todayKey });
  }
  return padded;
}

/**
 * Pick a y-axis domain for an integer-valued series: the smallest "nice" max
 * that is a whole multiple of `sections`, so every gridline lands on an integer
 * and no label repeats.
 *
 * A fixed floor (e.g. always 4) wastes half the plot when the user has learned
 * 1–2 characters — the line hugs the x-axis under a mostly-empty grid. Scaling
 * the domain to the data keeps the series legible from the very first character
 * while still leaving a section of headroom above the current max.
 */
export function niceIntegerAxisMax(maxValue: number, sections: number): number {
  if (!Number.isFinite(maxValue) || maxValue <= 0) {
    return sections;
  }
  // One section of headroom above the peak so the line never touches the top rule, with the
  // step snapped to a whole 1/2/5 × 10ⁿ so a count axis reads 0 / 50 / 100 rather than 0 / 42 / 84.
  const step = Math.max(1, niceStep(maxValue / (sections - 1)));
  return step * sections;
}

/**
 * Y-axis domain for a fractional series (percentages): the gridline step is snapped to a
 * 1/2/5 × 10ⁿ "nice" value so the axis reads cleanly at any magnitude (0.025% → 0.05%,
 * 3.1% → 5%), with one section of headroom above the peak. A user at 0.1% coverage still gets
 * real headroom instead of a line glued to the baseline. Never exceeds 100 — coverage is a
 * share of written Chinese, so the axis must not invent percentages beyond full coverage.
 */
export function nicePercentAxisMax(maxValue: number, sections: number): number {
  const capped = Math.min(100, Math.max(Number.isFinite(maxValue) ? maxValue : 0, 0));
  const rawStep = Math.max(capped, 0.1) / (sections - 1);
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const niceStep =
    (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
  return Math.min(100, niceStep * sections);
}

/**
 * Decimals needed to tell adjacent gridlines apart: a 0.05-step axis needs 2, a 5-step axis
 * needs 0. Without this every small-coverage label rounds to the same "0%".
 */
export function axisFractionDigits(step: number): number {
  return step >= 1 ? 0 : Math.min(4, Math.ceil(-Math.log10(step)));
}

export function shortDayLabel(dayKey: string): string {
  return new Date(dayKeyToMs(dayKey)).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
  });
}

export function fullDayLabel(dayKey: string): string {
  return new Date(dayKeyToMs(dayKey)).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * X-axis tick label for a timestamp, sized to the plotted span: day-level (`Sep 13`) for up to
 * about a year, month-level (`Sep 2026`) beyond that, where individual days no longer mean
 * anything at the tick spacing.
 */
export function formatTimelineTickLabel(ms: number, spanMs: number): string {
  return new Date(ms).toLocaleDateString(
    "en-US",
    spanMs > 400 * MS_PER_DAY
      ? { month: "short", year: "numeric" }
      : { day: "numeric", month: "short" },
  );
}

/** "in the past week" — the trailing clause of a delta caption for a bounded timeframe. */
export function timeframeWindowLabel(timeframe: Exclude<Timeframe, "All">): string {
  switch (timeframe) {
    case "1W": {
      return "in the past week";
    }
    case "1M": {
      return "in the past month";
    }
    default: {
      return "in the past year";
    }
  }
}

/**
 * The series a chart section plots for a timeframe: the timeline clipped to the window (with the
 * pre-cutoff total carried forward), then padded. The zero baseline is added only when the window
 * still starts at the user's first learning day — see {@link padTimelineForChart}.
 */
export function windowTimelineForChart(
  timeline: readonly LearnedTimelinePoint[],
  timeframe: Timeframe,
): readonly LearnedTimelinePoint[] {
  const windowed = filterTimeline(timeline, timeframe);
  const startsAtFirstLearningDay = windowed[0]?.dayKey === timeline[0]?.dayKey;
  return padTimelineForChart(windowed, { zeroBaseline: startsAtFirstLearningDay });
}

/** A y-axis window: `sections` equal steps from `floor` (≥ 0) up to `top`. */
export type AxisDomain = { readonly floor: number; readonly step: number; readonly top: number };

/**
 * Snap a raw step to 1/2/5 × 10ⁿ so gridline values read cleanly at any magnitude.
 */
function niceStep(rawStep: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  return (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
}

/**
 * Y-axis window for a cumulative series. Starts at 0 whenever the series does (or nearly does),
 * so lifetime views keep their true baseline. For a window that starts well above zero — the
 * past week of a user with hundreds of characters — a zero baseline would squash the actual
 * movement into the top sliver of the plot, so the floor is raised to a nice step just under the
 * minimum, leaving a section of headroom above the max. `integer` keeps the step whole so every
 * gridline of a count axis is a real count.
 */
export function niceAxisDomain(
  minValue: number,
  maxValue: number,
  sections: number,
  { integer }: { integer: boolean },
): AxisDomain {
  const max = Number.isFinite(maxValue) ? Math.max(maxValue, 0) : 0;
  const min = Number.isFinite(minValue) ? Math.max(Math.min(minValue, max), 0) : 0;
  const snap = (rawStep: number) => (integer ? Math.max(1, niceStep(rawStep)) : niceStep(rawStep));

  // Anchor at zero when the window already reaches (or nearly reaches) it: raising the floor
  // there would just clip the start of the series.
  if (min < max * 0.25) {
    const top = integer ? niceIntegerAxisMax(max, sections) : nicePercentAxisMax(max, sections);
    return { floor: 0, step: top / sections, top };
  }
  // Smallest nice step whose `sections` gridlines span the data with a little headroom above the
  // peak (a quarter step) and footroom below the minimum — walking up the 1/2/5 ladder from the
  // tightest possible step rather than snapping once, which could overshoot to twice the range.
  let step = snap(Math.max(max - min, integer ? 1 : 0.01) / sections);
  for (let attempt = 0; attempt < 12; attempt++) {
    let floor = Math.floor(min / step) * step;
    if (floor === min) {
      floor -= step;
    }
    floor = Math.max(0, floor);
    const top = floor + step * sections;
    if (top >= max + step * 0.25) {
      return { floor, step, top };
    }
    step = snap(step * 1.5);
  }
  const top = integer ? niceIntegerAxisMax(max, sections) : nicePercentAxisMax(max, sections);
  return { floor: 0, step: top / sections, top };
}

/** `[min, max]` of a plotted series' values (`[0, 0]` when empty). */
export function valueRange(points: readonly { readonly value: number }[]): [number, number] {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    min = Math.min(min, point.value);
    max = Math.max(max, point.value);
  }
  return Number.isFinite(min) ? [min, max] : [0, 0];
}
